import type { Form } from 'factorial-form'

import EmployeeSelector from 'modules/core/components/Inputs/EmployeeSelector'
import i18n from 'modules/core/lib/i18n'

import { employeeIsFuture, employeeReferenceContract } from 'modules/contracts/lib/utils'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import {
  employeeManagedEmployees,
  peopleManagedByEmployee,
} from 'modules/employees/store/collections/employeesContractVersions'
import type Employee from 'modules/employees/store/models/Employee'

interface Props {
  form: Form
  employee: Employee
  contractVersions: ContractVersions
}

const getDisabledEmployeeReason = (employee: Employee, contractVersions: ContractVersions) => {
  const startsOn = employeeReferenceContract(contractVersions, employee)?.startsOn?.fromNow()

  if (!startsOn) return

  return i18n.t('employees.starts', { date: startsOn })
}

const disabledEmployees = (employees: Employee[], contractVersions: ContractVersions): number[] => {
  return employees
    .filter((employee) => employeeIsFuture(contractVersions, employee))
    .map((employee) => employee.id)
}

const employeesList = (employee: Employee, contractVersions: ContractVersions): Employee[] => {
  const excludedEmployeeIds = [
    employee.id,
    ...employeeManagedEmployees(employee, contractVersions).map((e) => e.id),
  ]
  return employees.active.filter((e) => !excludedEmployeeIds.includes(e.id))
}

const ReassignManagerSelect = ({ form, employee, contractVersions }: Props) => {
  const getLabel = () =>
    !!peopleManagedByEmployee(employee.id, contractVersions).length
      ? i18n.t('employee_form.reassign_reports_to')
      : i18n.t('employee_form.reassign_to')

  const employees = employeesList(employee, contractVersions)

  return (
    <EmployeeSelector
      attachment="top center"
      employees={employees}
      field={form.get('manager_id')}
      label={getLabel()}
      multiFilters
      disabledEmployeeIds={disabledEmployees(employees, contractVersions)}
      getDisabledEmployeeReason={(employee) =>
        getDisabledEmployeeReason(employee, contractVersions)
      }
      readonly={!employees.length}
    />
  )
}

export default ReassignManagerSelect
